/*
 * Copyright (c) 2016-2018. campoint AG.
 * This file is licensed under the MIT License (https://opensource.org/licenses/MIT).
 */

import { StartStreamConfig } from '../start-stream-config';
import { AspectRatio } from './aspect-ratio';
import { SourceSet } from './source-set';

const defProtocols = ['jpeg', 'hls', 'rtmp', 'mp3', 'vorbis', 'webrtc', 'h5live'];

function computeGreatestCommonFactor(a: number, b: number) {
    let larger: number;
    let smaller: number;

    if (a > b) {
        larger = a;
        smaller = b;
    } else {
        larger = b;
        smaller = a;
    }

    let rest: number;

    do {
        rest = larger % smaller;
        larger = smaller;
        smaller = rest;
    } while (rest !== 0);

    return larger;
}

export function computeAspectRatio(w: number, h: number): AspectRatio {
    const gcf = computeGreatestCommonFactor(w, h);

    return {
        width: w / gcf,
        height: h / gcf,
    };
}

export function getSourceSet(
    vv: any,
    av: any,
    config: StartStreamConfig,
    protocols: string[] = defProtocols
): SourceSet {
    const videoDataUrl: string = vv.dataURL;
    const audioDataUrl: string = av.dataURL;

    const isMobile = config.mobile;

    const sourceSet: SourceSet = {};

    getInitialAspectRatio(sourceSet, vv);

    if (protocols.includes('jpeg') || protocols.includes('mjpeg')) {
        getJpegSources(config, protocols, vv, isMobile, sourceSet, videoDataUrl);
    }
    if (protocols.includes('hls')) {
        getHlsSource(isMobile, vv, sourceSet);
    }
    if (protocols.includes('rtmp')) {
        getRtmpSource(sourceSet, vv);
    }
    if (protocols.includes('webrtc')) {
        getWebRtcSource(sourceSet, vv);
    }
    if (protocols.includes('h5live')) {
        getH5LiveSource(sourceSet, vv);
    }

    if (vv?.hlsURL && protocols.includes('hls')) {
        sourceSet.hls.unshift({ stream: vv.hlsURL });
        sourceSet.preferredOrder = ['hls', 'hds', 'rtmp', 'mjpeg', 'jpeg'];
        if (!vv.hdsURL) {
            sourceSet.preferredOrder.splice(1, 1);
        }
    }

    if (av && audioDataUrl) {
        getAudioSources(protocols, sourceSet, audioDataUrl);
    }

    return sourceSet;
}

function getInitialAspectRatio(sourceSet: SourceSet, vv: any): void {
    if (vv === undefined) return;

    if (vv.width === undefined || vv.height === undefined) {
        return;
    }

    const w = Number.parseInt(vv.width);
    const h = Number.parseInt(vv.height);

    sourceSet.initialAspectRatio = computeAspectRatio(w, h);
}

function getAudioSources(protocols: string[], sourceSet: SourceSet, aDataUrl: string): void {
    if (protocols.includes('mp3')) {
        getMp3Source(sourceSet, aDataUrl);
    }
    if (protocols.includes('vorbis')) {
        getVorbisSource(sourceSet, aDataUrl);
    }
}

function getWebRtcSource(sourceSet: SourceSet, vv: any): void {
    if (vv.mediaWebRtc) {
        const webrtcData = JSON.parse(vv.mediaWebRtc);

        sourceSet.webrtc = [
            {
                stream: webrtcData.streamName,
                appName: webrtcData.appName,
                sdpUrl: webrtcData.signalingUrl,
            },
        ];
    }
}

function getH5LiveSource(sourceSet: SourceSet, vv: any): void {
    if (vv.mediaH5Live) {
        const h5Live = JSON.parse(vv.mediaH5Live);

        const dataUrl = new URL(vv.dataURL);
        const dataId = dataUrl.searchParams.get('dataID');

        sourceSet.h5live = [
            {
                server: h5Live.server,
                token: h5Live.securityToken,
                rtmp: h5Live.rtmp
                    ? {
                          url: h5Live.rtmp.appUrl,
                          streamname: h5Live.rtmp.streamName,
                      }
                    : undefined,
                bintu: h5Live.bintu
                    ? {
                          streamid: h5Live.bintu.streamid,
                          apiurl: h5Live.bintu.apiUrl,
                      }
                    : undefined,
                metrics: h5Live.metrics
                    ? {
                          id: h5Live.metrics.id,
                          key: h5Live.metrics.key,
                          dataId: dataId,
                      }
                    : undefined,
            },
        ];
    }
}

function getVorbisSource(sourceSet: SourceSet, aDataUrl: string): void {
    sourceSet.vorbis = [
        {
            stream: aDataUrl + '&format=13&f=stream.ogg',
        },
    ];
}

function getMp3Source(sourceSet: SourceSet, aDataUrl: string): void {
    sourceSet.mp3 = [
        {
            stream: aDataUrl + '&format=8&f=stream.mp3',
        },
    ];
}

function getJpegSources(
    config: StartStreamConfig,
    protocols: string[],
    vv: any,
    isMobile: boolean,
    sourceSet: SourceSet,
    vDataUrl: string
): void {
    const maxFps = config.maxFps > 0 ? config.maxFps : 15;
    let params = '';
    if (config.width) {
        params += `&width=${config.width}`;
    }
    if (config.height) {
        params += `&height=${config.height}`;
    }
    if (config.resizeMode) {
        params += `&sizemode=${config.resizeMode}`;
    }
    if (protocols.includes('jpeg')) {
        getJpegSource(vv, isMobile, params, sourceSet);
    }
    if (protocols.includes('mjpeg')) {
        getMJpegSource(sourceSet, vDataUrl, isMobile, maxFps, params);
    }
}

function getRtmpSource(sourceSet: SourceSet, vv: any): void {
    if (vv.mediaRtmpUrl) {
        const rtmpUrl = vv.mediaRtmpUrl;
        const rtmpUrlPattern = /(.*)\/(\d+)/;

        const rtmp = rtmpUrlPattern.exec(rtmpUrl);

        if (rtmp) {
            sourceSet.rtmp = [
                {
                    app: rtmp[1],
                    stream: rtmp[2],
                },
            ];
        }
    }
}

function getHlsSource(isMobile: boolean, vv: any, sourceSet: SourceSet): void {
    let hlsStreamUrl: string;
    if (isMobile) {
        hlsStreamUrl = vv.mediaHlsMobileUrl;
    } else {
        hlsStreamUrl = vv.mediaHlsUrl;
    }

    if (hlsStreamUrl) {
        sourceSet.hls = [
            {
                stream: hlsStreamUrl,
            },
        ];
    }
}

function getMJpegSource(
    sourceSet: SourceSet,
    vDataUrl: string,
    isMobile: boolean,
    maxFps: number,
    params: string
): void {
    sourceSet.mjpeg = [
        {
            stream:
                vDataUrl +
                (isMobile ? '&format=42' : '&format=20&quality=80') +
                `&fps=${maxFps}${params}`,
        },
    ];
}

function getJpegSource(vv: any, isMobile: boolean, params: string, sourceSet: SourceSet): void {
    if (vv.mediaJpegUrl) {
        const jpegSource =
            vv.mediaJpegUrl + (isMobile ? '&format=43' : '&format=15&quality=80') + params;
        sourceSet.jpeg = [
            {
                stream: jpegSource,
            },
        ];
    }
}
